import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../redux/store";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { IBasketTempItem } from "../../../../redux/cart/basketTemp/slice";
import { ITempProfileData } from "../../../../redux/profile/tempProfiles/slice";
import { clearBasketTemp } from "../../../../redux/cart/basketTemp/slice";
import { clearTempProfilesData } from "../../../../redux/profile/tempProfiles/slice";
import { setCartItems } from "../../../../redux/cart/slice";
import { validateChildBirthDate, formatDateToISOWithTime, formatDateToISO } from "../../../../helpers/dateFormatter";
import { useLookUpUsers } from "../../../../hooks/useLookUpUsers";
import { sendFrontendLog } from "../../../../redux/frontendLog/asyncActions";

interface IProfileItem {
  id: string;
  type: "adult" | "child" | "daily";
  profile: ITempProfileData;
  filled: boolean;
}

interface IFinalSubscription {
  id: string | number;
  type: string;
  title: string;
  price?: number;
  itemType: "subscription";
  groupId: number;
  addedAt: number;
  deliveryDate?: string;
  profiles: IProfileItem[];
}

export const useSubmitHandler = (
  basketTemp: IBasketTempItem[],
  tempProfilesData: { [profileId: string]: ITempProfileData },
  validateAllProfiles: boolean,
  deliveryDate: string,
  hasGift: boolean
) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getBitrixUser, registerBitrixUser } = useLookUpUsers();
  const { totalCartItems } = useSelector((state: RootState) => state.cart);
  const userPartnerId = useSelector((state: RootState) => state.user.partnerId);

  const handleSubmit = useCallback(async () => {
    if (!validateAllProfiles) {
      return;
    }

    const childValidationErrors: string[] = [];
    Object.keys(tempProfilesData).forEach((profileId) => {
      const profileData = tempProfilesData[profileId];
      if (profileData && profileData.birthDate) {
        const birthDateValidation = validateChildBirthDate(profileData.birthDate);
        if (!birthDateValidation.isValid) {
          childValidationErrors.push(
            `${profileData.name || "Профиль"} - ${birthDateValidation.error}`
          );
        }
      }
    });

    if (childValidationErrors.length > 0) {
      return;
    }

    const groupTimestamp = Date.now();
    const finalSubscriptions: IFinalSubscription[] = [];

    const subscriptionsWithFilledProfiles = basketTemp.map((subscription, index) => {
      if (subscription.type === "gift") {
        return {
          ...subscription,
          itemType: "subscription" as const,
          groupId: groupTimestamp,
          addedAt: groupTimestamp + index,
          profiles: [
            {
              id: "sender",
              type: "adult" as const,
              profile: tempProfilesData["sender"],
              filled: true,
            },
            {
              id: "receiver",
              type: "adult" as const,
              profile: tempProfilesData["receiver"],
              filled: true,
            },
          ],
          deliveryDate: deliveryDate,
        };
      } else {
        return {
          ...subscription,
          itemType: "subscription" as const,
          groupId: groupTimestamp,
          addedAt: groupTimestamp + index,
          profiles:
            subscription.profiles?.map((profile) => ({
              ...profile,
              profile: tempProfilesData[profile.id],
              filled: true,
            })) || [],
          deliveryDate: hasGift ? deliveryDate : undefined,
        };
      }
    });

    for (const subscription of subscriptionsWithFilledProfiles) {
      const updatedProfiles: IProfileItem[] = [];

      for (const profileItem of subscription.profiles || []) {
        let updatedProfileData = { ...profileItem.profile };

        try {
          let bitrixUser: string | number | null = null;

          if (profileItem.type === "adult" || profileItem.type === "daily") {
            bitrixUser = await getBitrixUser(
              updatedProfileData.phone,
              null,
              null,
              null,
              true
            );
          } else if (profileItem.type === "child") {
            if (updatedProfileData.birthDate && userPartnerId) {
              const birthDateISO = formatDateToISOWithTime(updatedProfileData.birthDate);
              
              bitrixUser = await getBitrixUser(
                null,
                userPartnerId.toString(),
                birthDateISO,
                updatedProfileData.name,
                true
              );
            }
          }

          if (!bitrixUser) {
            if (profileItem.type === "adult" || profileItem.type === "daily") {
              if (updatedProfileData.phone && updatedProfileData.name) {
                bitrixUser = await registerBitrixUser(
                  "adult",
                  updatedProfileData.phone,
                  updatedProfileData.email || "",
                  updatedProfileData.name,
                  null,
                  null,
                  true
                );
              }
            } else if (profileItem.type === "child") {
              if (updatedProfileData.birthDate && updatedProfileData.name && userPartnerId) {
                const birthDateISO = formatDateToISOWithTime(updatedProfileData.birthDate);
                
                bitrixUser = await registerBitrixUser(
                  "child",
                  null,
                  null,
                  updatedProfileData.name,
                  userPartnerId.toString(),
                  birthDateISO,
                  true
                );
              }
            }
          }

          if (bitrixUser) {
            updatedProfileData = { ...updatedProfileData, partner_id: bitrixUser };
          }
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message || error?.message || "";
          if (errorMessage.includes("Missing partner_id") || errorMessage.includes("partner_id")) {
            throw new Error("Missing partner_id");
          }
        }

        updatedProfiles.push({ ...profileItem, profile: updatedProfileData });
      }

      const updatedSubscription: IFinalSubscription = {
        ...subscription,
        profiles: updatedProfiles,
      };

      finalSubscriptions.push(updatedSubscription);
    }

    const subscriptionsForCart: { [key: string]: any } = {};
    finalSubscriptions.forEach((sub) => {
      const key = sub.id.toString();
      
      let quantity: number | number[];
      if (sub.type === "family") {
        const adultsCount = sub.profiles.filter(p => p.type === "adult" || p.type === "daily").length;
        const childrenCount = sub.profiles.filter(p => p.type === "child").length;
        quantity = [adultsCount, childrenCount];
      } else {
        quantity = sub.profiles.length;
      }
      
      subscriptionsForCart[key] = {
        subscription: {
          id: sub.id,
          title: sub.title,
          type: sub.type,
          price: sub.price,
        },
        quantity: quantity,
        profiles: sub.profiles.map((p) => ({
          name: p.profile.name,
          secondaryText:
            p.type === "child" ? p.profile.birthDate : p.profile.phone,
          type: p.type,
          partner_id: p.profile.partner_id,
        })),
        deliveryDate: sub.deliveryDate,
      };
    });

    const newTotalCartItems = {
      ...totalCartItems,
      subscriptions: {
        ...totalCartItems.subscriptions,
        ...subscriptionsForCart,
      },
    };

    dispatch(setCartItems(newTotalCartItems));

    try {
      dispatch(sendFrontendLog({
        type: "Subscriptions added to cart",
        data: {
          component: "useSubmitHandler",
          timestamp: new Date().toISOString(),
          url: window.location.pathname,
          userAgent: navigator.userAgent,
          subscriptionsCount: finalSubscriptions.length,
          totalProfiles: finalSubscriptions.reduce((sum, sub) => sum + (sub.profiles?.length || 0), 0),
          subscriptions: finalSubscriptions.map(sub => ({
            id: sub.id,
            type: sub.type,
            title: sub.title,
            price: sub.price,
            profilesCount: sub.profiles?.length || 0,
            hasDeliveryDate: !!sub.deliveryDate,
            deliveryDate: sub.deliveryDate,
            profiles: sub.profiles?.map(p => ({
              id: p.id,
              type: p.type,
              name: p.profile.name,
              phone: p.profile.phone,
              birthDate: p.profile.birthDate,
              email: p.profile.email,
              partner_id: p.profile.partner_id,
              filled: p.filled,
            })) || [],
          })),
          finalSubscriptions,
        },
      }));
    } catch (error) {
    }

    dispatch(clearBasketTemp());
    dispatch(clearTempProfilesData());

    navigate("/cart");
  }, [
    basketTemp,
    tempProfilesData,
    validateAllProfiles,
    deliveryDate,
    hasGift,
    dispatch,
    navigate,
    getBitrixUser,
    registerBitrixUser,
    totalCartItems,
  ]);

  return { handleSubmit };
};

